/**
 * Which timer controls a work order should offer, and why.
 *
 * Mechanics asked for start / pause / stop to be obvious in the task list
 * rather than buried, so the decision lives here as a pure function: the
 * card renders what this returns, and the rules are checkable without
 * mounting anything.
 *
 * These drive the **work-order level** routes, which are the ones that write
 * `work_order_time_entries` — the per-service buttons on each row only move
 * `booking_services.status` and leave the clock stopped. That is the whole
 * reason this exists: pressing play has to mean the clock is running.
 */

import type { BookingStatus } from '@/types';

export type TimerAction = 'start' | 'pause' | 'resume' | 'complete';

export interface TimerControls {
  /** Rendered first, as the primary button. Null once the work is done. */
  primary: 'start' | 'pause' | 'resume' | null;
  /** Finishing is offered alongside pause/resume, never before work began. */
  canComplete: boolean;
  /**
   * The vehicle is not here yet. The button still renders so its absence is
   * never mistaken for a missing feature, but it is disabled and explains
   * itself — same rule the per-service buttons already use.
   */
  blockedByArrival: boolean;
}

/**
 * Statuses that mean the car has not reached the workshop. Starting work on a
 * car that is not here would open a time entry against nobody's labour.
 */
const NOT_ARRIVED: readonly BookingStatus[] = [
  'quote',
  'pending',
  'confirmed',
  'quote_rejected',
];

/**
 * Past the point where a mechanic's clock is meaningful: the work is finished
 * and the order has moved into billing, or it is dead.
 */
const FINISHED: readonly BookingStatus[] = [
  'work_completed',
  'ready_to_invoice',
  // Attached to a collecting consolidated invoice: billing-locked like
  // ready_to_invoice, so the task-list clock stays off (#3941).
  'on_consolidated_invoice',
  'invoiced',
  'completed',
  'cancelled',
];

export function workOrderTimerControls(status: BookingStatus): TimerControls {
  if (FINISHED.includes(status)) {
    return { primary: null, canComplete: false, blockedByArrival: false };
  }

  if (NOT_ARRIVED.includes(status)) {
    // Offered but disabled: hiding it would read as "this card cannot be
    // started", which is wrong — it can, once the car arrives.
    return { primary: 'start', canComplete: false, blockedByArrival: true };
  }

  if (status === 'in_progress') {
    return { primary: 'pause', canComplete: true, blockedByArrival: false };
  }

  if (status === 'paused') {
    return { primary: 'resume', canComplete: true, blockedByArrival: false };
  }

  // Arrived and not yet started — `vehicle_arrived` plus the legacy scheduling
  // statuses, which still represent a car that can be worked on.
  return { primary: 'start', canComplete: false, blockedByArrival: false };
}
